/**
 * Escrow Payment Model - Fee Calculation Utilities
 *
 * This module implements the exact pricing, fee calculation, and payout logic
 * for the escrow-based transaction platform.
 *
 * Key properties:
 * - Free posting, monetization only via transactions
 * - Escrow protection + scam protection
 * - First deal free (platform fees waived) with ₹2,000 cap
 * - Dynamic buyer & seller platform fees
 */

// Constants
const GATEWAY_FEE_RATE = 0.02; // Cashfree 2% gateway fee
const FIRST_DEAL_CAP = 2000;

/**
 * Calculate buyer platform fee rate
 * Formula: b(P) = max(0.008, 0.016 - 0.001 * log10(P))
 */
export function calculateBuyerFeeRate(amount: number): number {
  if (amount <= 0) return 0.016;
  const rate = 0.016 - 0.001 * Math.log10(amount);
  return Math.max(0.008, rate);
}

/**
 * Calculate seller platform fee rate
 * Formula: s(P) = max(0.02, 0.05 - 0.003 * log10(P))
 */
export function calculateSellerFeeRate(amount: number): number {
  if (amount <= 0) return 0.05;
  const rate = 0.05 - 0.003 * Math.log10(amount);
  return Math.max(0.02, rate);
}

export interface EscrowCalculation {
  // Input
  requestedAmount: number;           // P - Seller's requested amount
  isFirstDealFree: boolean;

  // Buyer side
  buyerFeeRate: number;              // b(P) rate
  buyerPlatformFee: number;          // Amount buyer pays as platform fee
  buyerPays: number;                 // Total amount charged to buyer

  // Seller side
  sellerFeeRate: number;             // s(P) rate
  sellerPlatformFee: number;         // Amount deducted from seller
  sellerGets: number;                // Amount seller receives

  // Platform
  cashfreeFee: number;               // 2% of buyerPays
  platformRevenue: number;           // buyerPlatformFee + sellerPlatformFee
  platformProfit: number;            // platformRevenue - cashfreeFee

  // Display values
  buyerFeePercent: number;           // For UI display
  sellerFeePercent: number;          // For UI display
}

/**
 * Calculate complete escrow payment breakdown
 *
 * @param requestedAmount - P: Seller's requested transaction amount
 * @param isFirstDealFree - Whether first deal free promotion applies
 * @returns Complete breakdown of all fees and amounts
 */
export function calculateEscrowBreakdown(
  requestedAmount: number,
  isFirstDealFree: boolean = false
): EscrowCalculation {
  const P = requestedAmount;

  // Calculate fee rates
  const buyerFeeRate = calculateBuyerFeeRate(P);
  const sellerFeeRate = calculateSellerFeeRate(P);

  // First deal free logic: waive platform fees if P <= 2000
  const applyFirstDealFree = isFirstDealFree && P <= FIRST_DEAL_CAP;

  // Calculate platform fees
  let buyerPlatformFee: number;
  let sellerPlatformFee: number;

  if (applyFirstDealFree) {
    buyerPlatformFee = 0;
    sellerPlatformFee = 0;
  } else {
    buyerPlatformFee = P * buyerFeeRate;
    sellerPlatformFee = P * sellerFeeRate;
  }

  // Buyer pays: P + buyerPlatformFee
  const buyerPays = P + buyerPlatformFee;

  // Cashfree fee: 2% of total buyer payment (always applies)
  const cashfreeFee = GATEWAY_FEE_RATE * buyerPays;

  // Seller gets: P - sellerPlatformFee
  const sellerGets = P - sellerPlatformFee;

  // Platform revenue and profit
  const platformRevenue = buyerPlatformFee + sellerPlatformFee;
  const platformProfit = platformRevenue - cashfreeFee;

  return {
    requestedAmount: P,
    isFirstDealFree: applyFirstDealFree,

    buyerFeeRate,
    buyerPlatformFee,
    buyerPays,

    sellerFeeRate,
    sellerPlatformFee,
    sellerGets,

    cashfreeFee,
    platformRevenue,
    platformProfit,

    buyerFeePercent: buyerFeeRate * 100,
    sellerFeePercent: sellerFeeRate * 100,
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number, decimals: number = 2): string {
  return amount.toFixed(decimals);
}

/**
 * Get fee breakdown summary for seller
 * Used when seller is requesting payment
 */
export function getSellerPayoutSummary(
  requestedAmount: number,
  isFirstDealFree: boolean = false
): {
  requestedAmount: number;
  platformFee: number;
  platformFeePercent: number;
  youWillReceive: number;
  isFirstDealFree: boolean;
} {
  const breakdown = calculateEscrowBreakdown(requestedAmount, isFirstDealFree);

  return {
    requestedAmount: breakdown.requestedAmount,
    platformFee: breakdown.sellerPlatformFee,
    platformFeePercent: breakdown.sellerFeePercent,
    youWillReceive: breakdown.sellerGets,
    isFirstDealFree: breakdown.isFirstDealFree,
  };
}

/**
 * Get fee breakdown summary for buyer
 * Used in payment modal
 */
export function getBuyerPaymentSummary(
  dealAmount: number,
  isFirstDealFree: boolean = false
): {
  dealAmount: number;
  platformFee: number;
  platformFeePercent: number;
  totalToPay: number;
  isFirstDealFree: boolean;
} {
  const breakdown = calculateEscrowBreakdown(dealAmount, isFirstDealFree);

  return {
    dealAmount: breakdown.requestedAmount,
    platformFee: breakdown.buyerPlatformFee,
    platformFeePercent: breakdown.buyerFeePercent,
    totalToPay: breakdown.buyerPays,
    isFirstDealFree: breakdown.isFirstDealFree,
  };
}
